import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Explainer,
  Paragraph,
  TextInput,
  InputButton,
  useNotification
} from '@sopranodesign/pulse-components';
import Icon from '@sopranodesign/pulse-icons';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  connectZendesk,
  disconnectZendesk
} from '../api';
import WarningMessageBody from './WarningMessageBody';

// Shared with Zendesk.js / ZendeskKnowledgeBase.js so both refresh on connect/disconnect.
const ZENDESK_SETTINGS_QUERY_KEY = ['generative-ai/zendesk/settings'];
const ZENDESK_KB_QUERY_KEY = ['generative-ai/zendesk/'];

const ZendeskConnectionManager = ({
  activeModal,
  modalProps,
  closeModal,
  openModal
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [subdomain, setSubdomain] = useState('');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [showOauthClientSecret, setShowOauthClientSecret] = useState(false);
  const [connectError, setConnectError] = useState('');

  useEffect(() => {
    if (activeModal === 'updateCredential') {
      setOauthClientId(modalProps?.authentication?.oauthClientId ?? '');
      setOauthClientSecret(modalProps?.authentication?.oauthClientSecret ?? '');
      setConnectError(modalProps?.authentication?.error ?? '');
    }
  }, [activeModal, modalProps]);

  const invalidateZendeskQueries = () => {
    queryClient.invalidateQueries({ queryKey: ZENDESK_SETTINGS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ZENDESK_KB_QUERY_KEY });
  };

  const toggleOauthSecretVisibility = () => setShowOauthClientSecret(prev => !prev);

  const handleUpdateCredential = () => {
    if (!oauthClientId || !oauthClientSecret) {
      setConnectError('Please fill in all fields before updating credentials.');
      return;
    }

    setConnectError('');

    openModal('confirmation', { type: 'updateOauth' });
  };

  const handleConnect = async () => {
    if (!subdomain || !oauthClientId || !oauthClientSecret) {
      setConnectError('Please fill in all fields before connecting to Zendesk.');
      return;
    }

    setConnectError('');

    try {
      await connectZendesk({
        subdomain,
        oauth_client_id: oauthClientId,
        oauth_client_secret: oauthClientSecret
      });

      await queryClient.refetchQueries({ queryKey: ZENDESK_SETTINGS_QUERY_KEY });
      invalidateZendeskQueries();

      showSuccess('Zendesk connected successfully!');

      closeModal();
    } catch (error) {
      setConnectError(
        error.response?.data?.message ??
        error.response?.data?.error ??
        'Failed to connect.'
      );
    }
  };

  const confirmationActions = {
    updateOauth: {
      header: 'Confirm OAuth Credential Update',
      body: (
        <WarningMessageBody
          contentHeader="Please confirm this is the correct OAuth credential."
          contentBody="Please confirm it's the correct credential for the same Zendesk account currently connected to this workspace."
          warningText="If you wish to update it and the credentials belong to a different Zendesk instance, pre-existing synced content may be removed."
        />
      ),
      confirmText: 'Update Credentials',
      handleConfirm: async () => {
        try {
          await connectZendesk({
            oauth_client_id: oauthClientId,
            oauth_client_secret: oauthClientSecret,
            update: true
          });

          invalidateZendeskQueries();

          showSuccess('Zendesk OAuth credentials updated successfully.');

          closeModal();
        } catch (error) {
          const errorMessage =
            error.response?.data?.message ??
            error.response?.data?.error ??
            'Failed to update credentials.';

          closeModal();

          openModal('updateCredential', {
            authentication: {
              oauthClientId,
              oauthClientSecret,
              error: errorMessage
            }
          });
        }
      }
    },
    disconnect: {
      header: 'Confirm Disconnect Zendesk',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to disconnect Zendesk?"
          contentBody="All synced categories, articles, ticket filters and tickets will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Disconnect',
      handleConfirm: async () => {
        try {
          await disconnectZendesk();
          showSuccess('Zendesk disconnect requested');
          invalidateZendeskQueries();
          closeModal();
        } catch (error) {
          showError(`Failed to disconnect Zendesk: ${error.message}`);
        }
      }
    }
  };

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'connect':
      return (
        <Modal
          header="Connect Zendesk"
          handleCancel={closeModal}
          handleConfirm={handleConnect}
          confirmText="Connect"
          cancelText="Cancel"
          body={
            <Flex col gap="sm">
              {connectError && (
                <Explainer type="danger" mb hideLabel>
                  <Paragraph sm danger>
                    {connectError}
                  </Paragraph>
                </Explainer>
              )}

              <Flex fat col>
                <Label htmlFor="zendeskSubdomain">
                  Zendesk Subdomain
                </Label>

                <TextInput
                  id="zendeskSubdomain"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value)}
                />
              </Flex>

              <Flex fat col>
                <Label htmlFor="oauthClientId">
                  OAuth Client ID
                </Label>

                <TextInput
                  id="oauthClientId"
                  value={oauthClientId}
                  onChange={e => setOauthClientId(e.target.value)}
                />
              </Flex>

              <Flex fat col>
                <Label htmlFor="oauthClientSecret">
                  OAuth Client Secret
                </Label>

                <Flex relative>
                  <TextInput
                    id="oauthClientSecret"
                    type={showOauthClientSecret ? 'text' : 'password'}
                    value={oauthClientSecret}
                    onChange={e => setOauthClientSecret(e.target.value)}
                  />

                  <InputButton
                    type="button"
                    onClick={toggleOauthSecretVisibility}
                  >
                    <Icon
                      size="1.25rem"
                      width="1.25rem"
                      height="1.25rem"
                      type={showOauthClientSecret ? 'eye' : 'eyeOff'}
                    />
                  </InputButton>
                </Flex>
              </Flex>
            </Flex>
          }
        />
      );

    case 'updateCredential':
      return (
        <Modal
          header="Update Zendesk Credentials"
          handleCancel={closeModal}
          handleConfirm={handleUpdateCredential}
          confirmText="Update"
          cancelText="Cancel"
          body={
            <Flex col gap="sm">
              {connectError && (
                <Explainer type="danger" hideLabel>
                  <Paragraph sm danger>
                    {connectError}
                  </Paragraph>
                </Explainer>
              )}

              <Paragraph>
                Enter the OAuth Client ID and Secret to update the Zendesk connection.
              </Paragraph>

              <Flex fat col>
                <Label>
                  OAuth Client ID
                </Label>

                <TextInput
                  value={oauthClientId}
                  onChange={e => setOauthClientId(e.target.value)}
                />
              </Flex>

              <Flex fat col>
                <Label>
                  OAuth Client Secret
                </Label>

                <Flex relative>
                  <TextInput
                    type={showOauthClientSecret ? 'text' : 'password'}
                    value={oauthClientSecret}
                    onChange={e => setOauthClientSecret(e.target.value)}
                  />

                  <InputButton
                    type="button"
                    onClick={toggleOauthSecretVisibility}
                  >
                    <Icon
                      size="1.25rem"
                      width="1.25rem"
                      height="1.25rem"
                      type={showOauthClientSecret ? 'eye' : 'eyeOff'}
                    />
                  </InputButton>
                </Flex>
              </Flex>
            </Flex>
          }
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      if (!config) { return null; }
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

ZendeskConnectionManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.shape({
    type: PropTypes.string,
    authentication: PropTypes.shape({
      oauthClientId: PropTypes.string,
      oauthClientSecret: PropTypes.string,
      error: PropTypes.string
    })
  }),
  closeModal: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired
};

export { ZENDESK_SETTINGS_QUERY_KEY, ZENDESK_KB_QUERY_KEY };
export default ZendeskConnectionManager;
